import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Query-key families the analytics charts read, and the ones the live gate covers. */
export const CHART_SCOPES = ["blocks", "fees"] as const;

interface LiveChartsState {
  live: boolean;
  /**
   * Highest block the socket has announced this session, recorded whether or not the charts are
   * following it. Null until the first event arrives — no event is no evidence of being behind.
   *
   * A yardstick rather than a counter: a page compares it against the head its own data knows about,
   * so any refetch (a mount, a tab regaining focus, an explicit catch-up) closes the gap on its own.
   * Counting events instead left the badge claiming to be 300 blocks behind data that had just been
   * refetched underneath it.
   */
  chainHead: number | null;
  setLive: (live: boolean) => void;
  noteChainHead: (block: number) => void;
}

/**
 * Whether the block/fee charts follow the chain head as new blocks land.
 *
 * Those charts are read by holding a range still and comparing shapes inside it. Once mainnet got
 * busy, the `new_blocks` event arriving every few seconds slid the window forward and re-ran every
 * series animation under the reader's cursor, so following the head is now opt-in and off by
 * default. The preference persists — the reason to turn it off does not go away between visits.
 */
export const useLiveCharts = create<LiveChartsState>()(
  persist(
    (set) => ({
      live: false,
      chainHead: null,
      setLive: (live) => set({ live }),
      noteChainHead: (block) =>
        set((s) => (s.chainHead == null || block > s.chainHead ? { chainHead: block } : s)),
    }),
    { name: "clustec-live-charts", partialize: (s) => ({ live: s.live }) },
  ),
);
